import isNetworkError from 'is-network-error'

export const RETRYABLE_CODES = new Set([
  'ETIMEDOUT',
  'ECONNRESET',
  'ECONNREFUSED',
  'ENOTFOUND',
  'EPIPE',
  'EAI_AGAIN',
])

export const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504])

function getStatusCode(err: Record<string, unknown>): number | undefined {
  // .status (ServerError, fetch Response-based errors) or .statusCode (RequestError, Node http)
  const s = err.status ?? err.statusCode
  return typeof s === 'number' ? s : undefined
}

/** Classifies whether an error is retryable (network/transient). */
export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  // Network TypeError (Failed to fetch, etc)
  if (isNetworkError(error)) return true

  // HTTP status-based errors
  const status = getStatusCode(error)
  if (status !== undefined) return RETRYABLE_STATUS_CODES.has(status)

  // Socket/DNS errors from Node libs (pg, undici, etc)
  const code = (error as { code?: unknown }).code
  if (typeof code === 'string' && RETRYABLE_CODES.has(code)) return true

  // AggregateError (Node 20+ Happy Eyeballs — multiple connect failures wrapped)
  if ('errors' in error && Array.isArray((error as AggregateError).errors)) {
    return (error as AggregateError).errors.every((e) => isRetryableError(e))
  }

  return false
}

/** Extracts retry delay in ms from error's .retryAfterMs, or undefined. */
export function getRetryAfterMs(error: unknown): number | undefined {
  const ms = (error as { retryAfterMs?: unknown }).retryAfterMs
  if (typeof ms === 'number' && ms > 0) return ms
  return undefined
}

/**
 * Parses a Retry-After header value to ms.
 * Supports both delay-seconds ("120") and HTTP-date formats.
 * Returns undefined if unparseable.
 */
export function parseRetryAfterHeader(
  value: string | null | undefined,
): number | undefined {
  if (!value) return undefined

  // Try as seconds first
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds * 1000)

  // Try as HTTP-date
  const date = Date.parse(value)
  if (Number.isFinite(date)) {
    const ms = date - Date.now()
    return Math.max(ms, 0)
  }
  return undefined
}
